import { currencyFormatter, formatDate } from '../../utils/utils'

// Pure helpers that turn the backend's SubscriptionView / InvoiceView into what the tab renders.

/**
 * The six screen states (PRD §6.2) from GET /v1/billing/subscription:
 * 'network' | 'none' | 'trialing' | 'active' | 'past_due' | 'canceled'.
 */
export const deriveBillingState = (subscription) => {
  if (!subscription) return 'none'
  if (subscription.managedExternally) return 'network'
  if (!subscription.hasSubscription) return 'none'
  switch (subscription.state) {
    case 'TRIAL':
      return 'trialing'
    case 'ACTIVE':
      return 'active'
    case 'PAST_DUE':
      return 'past_due'
    case 'CANCELED':
      return 'canceled'
    default:
      return 'none'
  }
}

/**
 * Backend timestamps as a Date. Accepts ISO-8601 strings (what the billing API sends), but
 * also epoch seconds — the app's default Jackson setting for OffsetDateTime — and epoch
 * milliseconds, so a numeric payload can't render as "Jan 21, 1970". Null when unparseable.
 */
export const toDate = (value) => {
  if (value == null || value === '') return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  const numeric = typeof value === 'number' ? value : /^\d+(\.\d+)?$/.test(String(value).trim()) ? Number(value) : null
  if (numeric != null) {
    // Anything below 1e12 can't be a millisecond timestamp for a date after 2001 → seconds.
    return new Date(numeric < 1e12 ? numeric * 1000 : numeric)
  }
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export const BILLING_DATE_FORMAT = 'MMM D, YYYY'

/** "Sep 8, 2026" or '—' for a missing / unparseable date. */
export const formatBillingDate = (value) => {
  const date = toDate(value)
  return date ? formatDate(date, BILLING_DATE_FORMAT) : '—'
}

/** Whole days left until `value` (never negative). */
export const daysUntil = (value, now = new Date()) => {
  const date = toDate(value)
  if (!date) return 0
  return Math.max(0, Math.ceil((date.getTime() - now.getTime()) / 86400000))
}

/** Cents → "$1,799.00". */
export const formatCents = (cents) => currencyFormatter((cents ?? 0) / 100)

/** Invoice table row model from an InvoiceView. */
export const toInvoiceRow = (invoice, tierName) => ({
  key: invoice.number || invoice.hostedInvoiceUrl || `${invoice.createdAt}`,
  date: formatBillingDate(invoice.createdAt),
  description: invoice.number ? `${tierName} · ${invoice.number}` : `${tierName} · monthly`,
  total: formatCents(invoice.amountPaidCents ?? invoice.amountDueCents),
  status: invoice.status || 'open',
  href: invoice.invoicePdfUrl || invoice.hostedInvoiceUrl || null
})

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

/**
 * Human label for the payment method on file, or null when there is none. Stripe Link wallets
 * and bank accounts carry no card last4, so "on file" is decided by brand OR last4 — the
 * backend stores the method type as the brand for those.
 */
export const describePaymentMethod = ({ pmBrand, pmLast4 } = {}) => {
  if (pmLast4) return `${capitalize(pmBrand) || 'Card'} ending in ${pmLast4}`
  if (!pmBrand) return null
  if (pmBrand === 'link') return 'Link wallet'
  if (pmBrand === 'bank' || pmBrand === 'us_bank_account') return 'Bank account'
  return capitalize(pmBrand)
}

// Plan order (upgrade vs downgrade) moved to data/catalog.js `isUpgrade(catalog, from, to)` — it
// comes from each plan's rank in the database.

// "the limit of 10 $1 intro periods"; without a cap from the backend, "its limit of $1 intro periods".
const introLimit = (max) => (max > 0 ? `the limit of ${max} $1 intro periods` : 'its limit of $1 intro periods')

/**
 * Why a paid intro period was refused and its $1 refunded (backend trial_redemption): once per location, and up to
 * `maxIntros` (billing.intro.max-per-buyer, CAR-4014 — catalog `introMaxPerBuyer`) per card and per login.
 */
export const trialRefusalMessage = (code, maxIntros) => {
  switch (code) {
    case 'CARD_ALREADY_USED':
      return `This card has reached ${introLimit(maxIntros)}, so your $1 was refunded. Choose a plan to subscribe.`
    case 'ACCOUNT_ALREADY_USED':
      return `Your login has reached ${introLimit(maxIntros)}, so your $1 was refunded. Choose a plan to subscribe.`
    case 'LOCATION_ALREADY_USED':
      return 'This location has already had a package, so your $1 was refunded. Choose a plan to subscribe.'
    default:
      return null
  }
}
